type AdsConversionType = "purchase" | "lead";

type AdsConversionParams = {
  conversionType: AdsConversionType;
  transactionId?: string;
  value?: number;
  currency?: string;
};

export type AdsPurchaseParams = {
  transactionId: string;
  value: number;
  currency?: string;
};

type AdsLeadParams = {
  dedupeKey?: string;
};

const GOOGLE_ADS_ID = (process.env.REACT_APP_GOOGLE_ADS_ID || "").trim();
const GOOGLE_ADS_PURCHASE_CONVERSION_LABEL = (process.env.REACT_APP_GOOGLE_ADS_PURCHASE_CONVERSION_LABEL || "").trim();
const GOOGLE_ADS_LEAD_CONVERSION_LABEL = (process.env.REACT_APP_GOOGLE_ADS_LEAD_CONVERSION_LABEL || "").trim();

// TODO: Set REACT_APP_GOOGLE_ADS_LEAD_CONVERSION_LABEL in .env.local before enabling live lead conversion tracking.
const GOOGLE_ADS_LEAD_LABEL_PLACEHOLDER = "TODO_SET_REACT_APP_GOOGLE_ADS_LEAD_CONVERSION_LABEL";

const PURCHASE_DEDUPE_PREFIX = "ads:purchase:transaction:";
const LEAD_DEDUPE_PREFIX = "ads:lead:key:";

const inMemoryDedupe = new Set<string>();
let warnedMissingLeadLabel = false;

function normalizedLabelForConversion(type: AdsConversionType): string {
  if (type === "purchase") return GOOGLE_ADS_PURCHASE_CONVERSION_LABEL;
  return GOOGLE_ADS_LEAD_CONVERSION_LABEL;
}

function buildAdsSendTo(type: AdsConversionType): string | null {
  const label = normalizedLabelForConversion(type);
  if (!GOOGLE_ADS_ID || !label || label === GOOGLE_ADS_LEAD_LABEL_PLACEHOLDER) {
    if (type === "lead" && !warnedMissingLeadLabel) {
      warnedMissingLeadLabel = true;
      console.warn(
        "[ads] Lead conversion label missing. Set REACT_APP_GOOGLE_ADS_LEAD_CONVERSION_LABEL to enable low-tier lead conversion tracking."
      );
    }
    return null;
  }
  return `${GOOGLE_ADS_ID}/${label}`;
}

function hasSentInSession(dedupeKey: string): boolean {
  if (typeof window === "undefined") return true;
  const key = `approbot:${dedupeKey}`;

  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return inMemoryDedupe.has(key);
  }
}

function markSentInSession(dedupeKey: string) {
  if (typeof window === "undefined") return;
  const key = `approbot:${dedupeKey}`;

  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    inMemoryDedupe.add(key);
  }
}

function sendAdsConversion(params: AdsConversionParams): boolean {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return false;
  }

  const sendTo = buildAdsSendTo(params.conversionType);
  if (!sendTo) return false;

  const payload: Record<string, string | number> = { send_to: sendTo };
  if (params.transactionId) payload.transaction_id = params.transactionId;
  if (typeof params.value === "number") payload.value = params.value;
  if (params.currency) payload.currency = params.currency;

  window.gtag("event", "conversion", payload);
  return true;
}

/**
 * Purchase conversion = High + Mid tiers only, fired after Stripe payment confirmation.
 */
export function trackAdsPurchase(params: AdsPurchaseParams): boolean {
  const transactionId = params.transactionId.trim();
  if (!transactionId) return false;

  const dedupeKey = `${PURCHASE_DEDUPE_PREFIX}${transactionId}`;
  if (hasSentInSession(dedupeKey)) return false;

  const sent = sendAdsConversion({
    conversionType: "purchase",
    transactionId,
    value: params.value,
    currency: params.currency || "USD",
  });
  if (sent) markSentInSession(dedupeKey);
  return sent;
}

/**
 * Lead conversion = Low-tier free signup/download success, fired once per session dedupe key.
 */
export function trackAdsLead(params?: AdsLeadParams): boolean {
  const dedupeKey = (params?.dedupeKey || "low-tier-lead").trim();
  const fullDedupeKey = `${LEAD_DEDUPE_PREFIX}${dedupeKey}`;
  if (hasSentInSession(fullDedupeKey)) return false;

  const sent = sendAdsConversion({
    conversionType: "lead",
    transactionId: `lead_${dedupeKey}`,
    value: 0,
    currency: "USD",
  });
  if (sent) markSentInSession(fullDedupeKey);
  return sent;
}

export function trackAdsBeginCheckout(params: { value: number; currency?: string }): boolean {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return false;
  window.gtag("event", "begin_checkout", {
    value: params.value,
    currency: params.currency || "USD",
  });
  return true;
}
